/**
 * Custom hook for video platform API operations
 * Centralizes all API calls with proper authentication
 */

import {
  VideoPlatformResource,
  MeetingCategoryOption,
  BatchCapabilityOperation,
} from "../../../api/video_platform";
import { CreateMeetingFormData } from "../types";

export const useVideoPlatformApi = () => {
  const createMeeting = async (formData: CreateMeetingFormData) => {
    return VideoPlatformResource.createMeeting(formData);
  };

  const deleteMeeting = async (meetingId: string) => {
    return VideoPlatformResource.deleteMeeting(meetingId);
  };

  const refreshAttendees = (meetingId: string) => {
    return VideoPlatformResource.getMeetingAttendees({ meetingId });
  };

  const updateAttendeeCapabilities = async (
    meetingId: string,
    attendeeId: string,
    capabilities: {
      audio?: { value?: string; lock?: boolean };
      video?: { value?: string; lock?: boolean };
      content?: { value?: string; lock?: boolean };
      chat?: { value?: string; lock?: boolean };
    }
  ) => {
    return VideoPlatformResource.updateAttendeeCapabilities(
      meetingId,
      attendeeId,
      capabilities
    );
  };

  const batchUpdateAttendeeCapabilities = async (
    meetingId: string,
    operation: BatchCapabilityOperation,
    excludeHosts: boolean = true,
    lock: boolean = false
  ) => {
    return VideoPlatformResource.batchUpdateAttendeeCapabilities(meetingId, {
      operation,
      excludeHosts,
      lock,
    });
  };

  /** Ban an attendee. Returns ok even if already banned (idempotent). */
  const banAttendee = async (
    meetingId: string,
    attendeeId: string,
    reason?: string
  ) => {
    return VideoPlatformResource.banAttendee(meetingId, attendeeId, reason);
  };

  /** Lift a ban (idempotent: wasBanned=false when no ban was in effect). */
  const unbanAttendee = async (meetingId: string, attendeeId: string) => {
    return VideoPlatformResource.unbanAttendee(meetingId, attendeeId);
  };

  const getCategories = async (): Promise<{
    categories: MeetingCategoryOption[];
  }> => {
    return VideoPlatformResource.getCategories();
  };

  const updateMeeting = async (
    meetingId: string,
    data: {
      category?: string;
      title?: string;
      description?: string;
      isPublicVisible?: boolean;
      useFallback?: boolean;
      fallbackMeetUrl?: string | null;
    }
  ) => {
    return VideoPlatformResource.updateMeeting(meetingId, data);
  };

  return {
    createMeeting,
    deleteMeeting,
    refreshAttendees,
    updateAttendeeCapabilities,
    batchUpdateAttendeeCapabilities,
    banAttendee,
    unbanAttendee,
    getCategories,
    updateMeeting,
  };
};
